import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Clock, ArrowRight, Home, CheckCircle, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { locationPages } from '../../data/seoContent';
import { properties } from '../../mock';

const LocationPage = () => {
  const { slug } = useParams();
  const location = locationPages.find(l => l.slug === slug);

  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Location Not Found</h1>
          <Link to="/locations" className="text-amber-600 hover:underline">
            View All Locations
          </Link>
        </div>
      </div>
    );
  }

  const nearbyLocations = locationPages.filter(l => 
    location.nearbyLocations?.includes(l.slug)
  ).slice(0, 4);

  const featuredProperties = properties.filter(p => p.featured).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{location.metaTitle}</title>
        <meta name="description" content={location.metaDescription} />
        <meta name="keywords" content={location.keywords} />
        <link rel="canonical" href={`https://capitol-view-stay.com/locations/${slug}`} />
        <meta property="og:title" content={location.metaTitle} />
        <meta property="og:description" content={location.metaDescription} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[50vh] min-h-[400px] flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${location.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <p className="text-amber-400 font-medium mb-2">Capitol View Stays</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{location.title}</h1>
          <p className="text-xl max-w-2xl mx-auto opacity-90">{location.intro}</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-slate-100 py-3">
        <div className="container mx-auto px-4">
          <nav className="text-sm text-slate-600">
            <Link to="/" className="hover:text-amber-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/locations" className="hover:text-amber-600">Locations</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-900">{location.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Why Stay Here */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Why Stay Near {location.title.replace('Vacation Rentals Near ', '')}?
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {location.whyStay.map((reason, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Nearby Attractions */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Nearby Attractions</h2>
                <div className="space-y-4">
                  {location.attractions.map((attraction, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">{attraction.name}</h3>
                            <p className="text-slate-600 text-sm mt-1">{attraction.description}</p>
                          </div>
                          <div className="flex items-center gap-1 text-amber-600 text-sm whitespace-nowrap">
                            <MapPin className="h-4 w-4" />
                            {attraction.distance}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Main Article Content */}
              <div className="prose prose-slate max-w-none">
                <div dangerouslySetInnerHTML={{ __html: location.content.replace(/\n/g, '<br/>').replace(/## /g, '<h2>').replace(/### /g, '<h3>').replace(/<h2>/g, '</p><h2 class="text-2xl font-bold text-slate-900 mt-8 mb-4">').replace(/<h3>/g, '</p><h3 class="text-xl font-semibold text-slate-800 mt-6 mb-3">').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              </div>

              {/* Internal Links */}
              <div className="mt-12 p-6 bg-slate-50 rounded-xl">
                <h3 className="font-bold text-slate-900 mb-4">Explore More DC Locations</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {nearbyLocations.map((loc) => (
                    <Link 
                      key={loc.slug}
                      to={`/locations/${loc.slug}`}
                      className="flex items-center gap-2 text-slate-700 hover:text-amber-600 transition-colors"
                    >
                      <ArrowRight className="h-4 w-4" />
                      {loc.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* CTA Card */}
              <Card className="sticky top-24 mb-8 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className="inline-block px-3 py-1 bg-amber-500 text-slate-900 text-sm font-bold rounded-full mb-3">
                      SAVE 15% BOOKING DIRECT
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">Book Your DC Vacation Rental</h3>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Fast WiFi for remote work
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Self check-in available
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Family-friendly accommodations
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Professional cleaning
                    </div>
                  </div>

                  <Link to="/properties">
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                      Browse Properties
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Featured Properties */}
              <div className="space-y-4">
                <h3 className="font-bold text-slate-900">Featured Properties</h3>
                {featuredProperties.map((property) => (
                  <Link key={property.id} to={`/property/${property.id}`}>
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex gap-3">
                          <img 
                            src={property.images[0]} 
                            alt={property.name}
                            className="w-20 h-20 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-900 text-sm truncate">
                              {property.name}
                            </h4>
                            <div className="flex items-center gap-1 text-amber-500 text-xs mt-1">
                              <Star className="h-3 w-3 fill-current" />
                              <Star className="h-3 w-3 fill-current" />
                              <Star className="h-3 w-3 fill-current" />
                              <Star className="h-3 w-3 fill-current" />
                              <Star className="h-3 w-3 fill-current" />
                            </div>
                            <p className="text-slate-600 text-xs mt-1">
                              {property.bedrooms} BR • Sleeps {property.sleeps}
                            </p>
                            <p className="text-amber-600 font-bold text-sm mt-1">
                              ${property.pricePerNight}/night
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Book Your Washington DC Vacation Rental?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Browse Capitol View Stays properties and book directly to save 15%. 
            Experience the best of Washington DC with our premium, fully-furnished rentals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                View All Properties
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LocationPage;
